/**
 * ADR-0248 §4.4 — detail panel for one probe-campaign.
 *
 * Members list (flag / country-region / masked-or-shown IP / request
 * counts), shared paths, networks the campaign spans, and the
 * recommendation sentence (addresses rotate; protect the paths this
 * campaign targets instead). No "Block all addresses" button.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { Card, RelativeTime } from '../../components/atoms/index.js';
import { CountryFlag, countryName } from '../../components/atoms/CountryFlag.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { getThreatCampaign } from '../../api/threats.js';
import { useC } from '../../lib/theme.js';
import type {
  ThreatCampaignDetailResponse,
  ThreatCampaignMember,
  ThreatCampaignSummary,
} from '@sill/types/api';
import { CampaignAutoBlockSection } from './CampaignAutoBlockSection.js';
import { severityIcon } from './severity-icon.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

type State =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly message: string }
  | { readonly kind: 'loaded'; readonly data: ThreatCampaignDetailResponse };

export interface CampaignDetailProps {
  readonly siteId: string;
  /** Site domain shown in the auto-block confirm dialog. ADR-0249 §Decision B. */
  readonly siteDomain?: string;
  readonly campaign: ThreatCampaignSummary;
  readonly onSelectMember?: (sender_ref: string) => void;
  /** Fires after a blocking change (auto-block on / undo). */
  readonly onChanged?: () => void;
}

function locationLine(member: ThreatCampaignMember): string | null {
  const country = countryName(member.country) ?? member.country ?? null;
  const region = typeof member.region === 'string' && member.region.length > 0 ? member.region : null;
  if (country === null && region === null) return null;
  if (country === null) return region;
  if (region === null) return country;
  return `${region}, ${country}`;
}

export function CampaignDetail({ siteId, siteDomain, campaign, onSelectMember, onChanged }: CampaignDetailProps): JSX.Element {
  const C = useC();
  const http = useOptionalHttp();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    if (http === null) {
      setState({ kind: 'error', message: 'The dashboard is offline.' });
      return;
    }
    setState({ kind: 'loading' });
    let live = true;
    getThreatCampaign(http, siteId, campaign.campaign_id)
      .then((data) => {
        if (!live) return;
        if (data === null) setState({ kind: 'error', message: 'This campaign is no longer visible.' });
        else setState({ kind: 'loaded', data });
      })
      .catch(() => {
        if (live) setState({ kind: 'error', message: 'Could not load this campaign.' });
      });
    return () => { live = false; };
  }, [http, siteId, campaign.campaign_id]);

  const sev = severityIcon(campaign.severity, C);

  const renderHeader = (): ReactNode => (
    <div>
      <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted }}>
        CAMPAIGN &middot; {campaign.dominant_class_label.toUpperCase()}
      </div>
      <h2
        data-testid="campaign-detail-title"
        style={{
          fontFamily: serifFont,
          fontSize: 22,
          color: C.text,
          marginTop: 6,
          marginBottom: 8,
          fontWeight: 400,
          lineHeight: 1.25,
        }}
      >
        {campaign.headline}
      </h2>
      <div style={{ fontFamily: monoFont, fontSize: 10, color: C.muted, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {sev.node}
          <span>{campaign.severity}</span>
        </span>
        <span>&middot; first</span>
        <RelativeTime iso={campaign.first_seen} style={{ fontFamily: monoFont, fontSize: 10, color: C.body }} />
        <span>&middot; last</span>
        <RelativeTime iso={campaign.last_seen} style={{ fontFamily: monoFont, fontSize: 10, color: C.body }} />
      </div>
    </div>
  );

  if (state.kind === 'loading') {
    return (
      <Card>
        <div style={{ padding: 20 }}>
          {renderHeader()}
          <div style={{ marginTop: 20, color: C.muted, fontFamily: sansFont, fontSize: 13 }}>
            Loading campaign details.
          </div>
        </div>
      </Card>
    );
  }
  if (state.kind === 'error') {
    return (
      <Card>
        <div style={{ padding: 20 }}>
          {renderHeader()}
          <div style={{ marginTop: 20, color: C.muted, fontFamily: sansFont, fontSize: 13 }}>
            {state.message}
          </div>
        </div>
      </Card>
    );
  }
  const data = state.data;
  return (
    <Card>
      <div style={{ padding: 20 }}>
        {renderHeader()}

        {/* Recommendation */}
        <div
          data-testid="campaign-recommendation"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.body,
            lineHeight: 1.5,
            marginTop: 16,
            padding: 12,
            border: `1px solid ${C.border}`,
            background: C.surface2,
          }}
        >
          {data.recommendation}
        </div>

        {/* ADR-0249 S5 — Auto-block this campaign. Primary action lives
            INSIDE the detail panel so the member list stays the context
            for the decision. Confirm dialog names the domain + count + 7-day
            expiry; armed state swaps for a count + Undo chip. */}
        <CampaignAutoBlockSection
          siteId={siteId}
          siteDomain={siteDomain ?? ''}
          campaign={campaign}
          currentMemberCount={data.members.length}
          {...(onChanged !== undefined ? { onChanged } : {})}
        />

        {/* Members */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 8 }}>
            ADDRESSES &middot; {campaign.sender_count}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {data.members.map((m, i) => {
              const location = locationLine(m);
              const row_border = i === data.members.length - 1 ? 'none' : `1px solid ${C.borderSoft}`;
              return (
                <button
                  key={`${m.sender_ref}-${i}`}
                  type="button"
                  data-testid="campaign-member-row"
                  data-sender-ref={m.sender_ref}
                  onClick={() => onSelectMember?.(m.sender_ref)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    padding: '10px 0',
                    borderBottom: row_border,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    minWidth: 0,
                  }}
                >
                  {m.country !== null ? (
                    <span style={{ flexShrink: 0 }}>
                      <CountryFlag code={m.country} size={16} />
                    </span>
                  ) : (
                    <span style={{ flexShrink: 0, width: 16 }} />
                  )}
                  <span
                    style={{
                      fontFamily: monoFont,
                      fontSize: 11,
                      color: C.text,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}
                  >
                    {m.sender_ip_display}
                    {m.ip_status === 'hidden' ? (
                      <span style={{ color: C.muted, marginLeft: 6, fontSize: 9 }}>(masked)</span>
                    ) : null}
                    <span style={{ color: C.muted, marginLeft: 6 }}>
                      &middot; {location ?? 'location unknown'}
                    </span>
                  </span>
                  <span style={{ fontFamily: monoFont, fontSize: 10, color: C.body, flexShrink: 0 }}>
                    {m.request_count.toLocaleString('en-US')}
                    {m.any_succeeded ? (
                      <span style={{ color: C.coralFg, marginLeft: 6 }}>&middot; succeeded</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Networks */}
        {data.networks.length > 0 ? (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 8 }}>
              NETWORKS
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {data.networks.map((n, i) => (
                <div
                  key={`net-${n.asn ?? 'null'}-${i}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.body,
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {n.as_org ?? 'Unknown organisation'}
                    {n.asn !== null ? (
                      <span style={{ color: C.muted }}> (AS{n.asn})</span>
                    ) : null}
                  </span>
                  <span style={{ color: C.muted }}>{n.sender_count} address{n.sender_count === 1 ? '' : 'es'}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Shared paths */}
        {data.shared_paths.length > 0 ? (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 8 }}>
              SHARED PATHS
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {data.shared_paths.map((p, i) => (
                <div
                  key={`path-${i}`}
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.body,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
